// What? A React component for viewing and managing user documents.
// Why? To provide a central interface for users to view, edit, download, upgrade, and delete their generated documents.
// How? The component's logic flow:
// 1. Initialize: Fetch documents for the current workspace
// 2. Display Management: Show documents in responsive table (desktop) or cards (mobile)
// 3. Document Actions: Handle upgrade, delete, download, and edit operations
// 4. Modal Handling: Manage confirmation modals for destructive actions
// 5. Loading States: Show loading indicators during data fetch and operations

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Table,
  Box,
  Chip,
  CircularProgress,
  Typography,
  Stack,
  Card,
  CardContent,
} from "@mui/joy";
import {
  Upgrade as UpgradeIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FactCheck as FactCheckIcon,
} from "@mui/icons-material";
import Tooltip from "@mui/joy/Tooltip";
import IconButton from "@mui/joy/IconButton";

import Page from "../../../../components/Pages/Page";
import PageHeader from "../../../../components/PageHeader";
import ConfirmationModal from "../../../../components/Modals/ConfirmationModal";
import Loading from "../../../../components/Loading";
import { useTutorialTarget, useTutorialHint } from "../../../../components/Tutorial";
import { useFetchUserDocuments, useDocuments } from "../../hooks/useDocuments";
import {
  useFetchDocumentTypes,
  useFetchPublishedDocumentTypes,
} from "../../hooks/useDocumentTypes";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import { Document } from "../../types/documentTypes";
import QCModal from "../../components/qc/QCModal";
import { useFeatureFlag } from "../../../../providers/FeatureFlagProvider";

interface UpgradeModalState {
  isOpen: boolean;
  document?: Document;
}

interface DeleteModalState {
  isOpen: boolean;
  document?: Document;
}

const DocumentOverview: React.FC = () => {
  const { workspace, isAdmin } = useWorkspaceContext();
  const workspace_id = workspace?.id || "";
  const { documents, loading, error, refresh } =
    useFetchUserDocuments(workspace_id);
  const { upgradeDocument, deleteDocument, downloadFileToBrowser } =
    useDocuments();
  const [upgradeModal, setUpgradeModal] = useState<UpgradeModalState>({
    isOpen: false,
    document: undefined,
  });
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    isOpen: false,
    document: undefined,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const qcEnabled = useFeatureFlag("meridian_qc");
  const [qcModal, setQcModal] = useState<{
    isOpen: boolean;
    document: Document | null;
  }>({ isOpen: false, document: null });
  const navigate = useNavigate();

  // All doc types (any status) — admin only; the nav hint fires only when zero
  // doc types exist (not merely when none are published yet).
  const { documentTypes: allDocTypes, loading: loadingAllDocTypes } =
    useFetchDocumentTypes(workspace_id);

  // Published doc types — accessible to all roles; needed for the generate hint.
  const { documentTypes: publishedDocTypes, loading: loadingDocTypes } =
    useFetchPublishedDocumentTypes(workspace_id);
  const hasPublishedDocTypes =
    !loadingDocTypes && publishedDocTypes != null && publishedDocTypes.length > 0;

  const generateDocRef = useTutorialTarget("meridian-generate-doc");

  useTutorialHint(
    {
      id: "meridian-navigate-to-doc-types",
      targetId: "meridian-doc-types-nav",
      title: "Create a Document Type first",
      body: "Document Types define the structure for AI-generated documents. Click here to create and publish one before generating documents.",
      placement: "right",
      scope: "workspace",
    },
    isAdmin && !loading && !loadingAllDocTypes && allDocTypes != null && allDocTypes.length === 0,
  );

  useTutorialHint(
    {
      id: "meridian-first-document",
      targetId: "meridian-generate-doc",
      title: "Generate your first document",
      body: "Select a document type and fill in the required inputs to generate your first AI-assisted document.",
      placement: "bottom",
      scope: "workspace",
    },
    !loading && !error && hasPublishedDocTypes && (!documents || documents.length === 0),
  );

  const handleGenerateNew = () => {
    navigate(`/workspace/${workspace_id}/meridian/documents/new`);
  };

  const handleEditDocument = (documentId: number) => {
    navigate(
      `/workspace/${workspace_id}/meridian/documents/edit/${documentId}`,
    );
  };

  const handleUpgradeDocument = async (documentId: number) => {
    setUpgradeLoading(true);
    try {
      await upgradeDocument(documentId, workspace_id);
      setUpgradeModal({ isOpen: false });
      refresh();
    } catch (error) {
      // Optionally show error
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    setDeleteLoading(true);
    try {
      await deleteDocument(documentId, workspace_id);
      setDeleteModal({ isOpen: false });
      refresh();
    } catch (error) {
      // Optionally show error
    } finally {
      setDeleteLoading(false);
    }
  };

  // Helper function for status chip colors
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Completed":
        return "success";
      case "Generated":
        return "primary";
      case "Started":
        return "warning";
      default:
        return "neutral";
    }
  };

  // Component for individual document cards (mobile view)
  const DocumentCard = ({ doc }: { doc: Document }) => (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "sm",
          borderColor: "primary.300",
        },
      }}
    >
      <Stack spacing={2}>
        {/* Top row: Title and Status */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              level="title-sm"
              sx={{
                fontWeight: 600,
                wordBreak: "break-word",
                lineHeight: 1.3,
              }}
            >
              {doc.Title}
            </Typography>
            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
              {doc.DocumentType.name} (v{doc.DocumentType.version})
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Chip
              size="sm"
              variant="soft"
              color={getStatusColor(doc.Status) as any}
            >
              {doc.Status}
            </Chip>
          </Box>
        </Stack>

        {/* Middle row: Dates */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography level="body-xs" color="neutral">
              <Typography
                component="span"
                level="body-xs"
                color="neutral"
                sx={{ fontWeight: 500 }}
              >
                Created:{" "}
              </Typography>
              {new Date(doc.CreatedDate).toLocaleDateString()}
            </Typography>
          </Box>
          {doc.ModifiedDate && (
            <Box>
              <Typography level="body-xs" color="neutral">
                <Typography
                  component="span"
                  level="body-xs"
                  color="neutral"
                  sx={{ fontWeight: 500 }}
                >
                  Modified:{" "}
                </Typography>
                {new Date(doc.ModifiedDate).toLocaleDateString()}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Bottom row: Actions */}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {doc.DocumentType.version < doc.DocumentType.latestVersion && (
            <Tooltip title="Upgrade">
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => setUpgradeModal({ isOpen: true, document: doc })}
              >
                {upgradeLoading &&
                upgradeModal.document?.DocumentID === doc.DocumentID ? (
                  <CircularProgress size="sm" />
                ) : (
                  <UpgradeIcon />
                )}
              </IconButton>
            </Tooltip>
          )}
          {doc.LatestExportFilePath && (
            <Tooltip title="Download">
              <IconButton
                size="sm"
                variant="plain"
                onClick={() =>
                  downloadFileToBrowser(doc.LatestExportFilePath!, workspace_id)
                }
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
          {qcEnabled &&
            (doc.SourceDocumentCount ?? doc.sourceDocuments?.length ?? 0) > 0 &&
            (doc.Status === "Generated" || doc.Status === "Completed") && (
              <Tooltip title="Quality Check">
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={() => setQcModal({ isOpen: true, document: doc })}
                >
                  <FactCheckIcon />
                </IconButton>
              </Tooltip>
            )}
          {doc.Status !== "Completed" && (
            <Tooltip title="Edit">
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => handleEditDocument(doc.DocumentID)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => setDeleteModal({ isOpen: true, document: doc })}
              disabled={deleteLoading}
            >
              <DeleteIcon sx={{ color: "#c41c1c" }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">My Documents</Typography>
        <span ref={generateDocRef}>
          <Button
            variant="solid"
            color="primary"
            startDecorator={<AddIcon />}
            onClick={handleGenerateNew}
            sx={{
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            Generate New Document
          </Button>
        </span>
      </PageHeader>

      <Card>
        <CardContent>
          {loading ? (
            <Loading />
          ) : error ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography level="body-md" sx={{ mb: 2 }}>
                Error loading documents: {error}
              </Typography>
              <Button variant="outlined" onClick={refresh}>
                Retry
              </Button>
            </Box>
          ) : !documents || documents.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography level="body-md" sx={{ mb: 1 }}>
                No documents yet
              </Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
                Generate your first document to get started
              </Typography>
              <Button
                variant="solid"
                color="primary"
                startDecorator={<AddIcon />}
                onClick={handleGenerateNew}
              >
                Generate New Document
              </Button>
            </Box>
          ) : (
            <>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: "none", lg: "block" } }}>
                <Table sx={{ "& td, & th": { verticalAlign: "top" } }}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th style={{ width: "180px" }}>Document Type</th>
                      <th style={{ width: "120px" }}>Status</th>
                      <th style={{ width: "120px" }}>Created</th>
                      <th style={{ width: "120px" }}>Last Modified</th>
                      <th style={{ width: "210px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc.DocumentID}>
                        <td>{doc.Title}</td>
                        <td>
                          {doc.DocumentType.name} (v{doc.DocumentType.version})
                        </td>
                        <td>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={getStatusColor(doc.Status) as any}
                          >
                            {doc.Status}
                          </Chip>
                        </td>
                        <td>
                          {new Date(doc.CreatedDate).toLocaleDateString()}
                        </td>
                        <td>
                          {doc.ModifiedDate
                            ? new Date(doc.ModifiedDate).toLocaleDateString()
                            : "-"}
                        </td>
                        <td>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {doc.DocumentType.version <
                              doc.DocumentType.latestVersion && (
                              <Tooltip title="Upgrade">
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  onClick={() =>
                                    setUpgradeModal({
                                      isOpen: true,
                                      document: doc,
                                    })
                                  }
                                >
                                  {upgradeLoading &&
                                  upgradeModal.document?.DocumentID ===
                                    doc.DocumentID ? (
                                    <CircularProgress size="sm" />
                                  ) : (
                                    <UpgradeIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            {doc.LatestExportFilePath && (
                              <Tooltip title="Download">
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  onClick={() =>
                                    downloadFileToBrowser(
                                      doc.LatestExportFilePath!,
                                      workspace_id,
                                    )
                                  }
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {qcEnabled &&
                              (doc.SourceDocumentCount ??
                                doc.sourceDocuments?.length ??
                                0) > 0 &&
                              (doc.Status === "Generated" ||
                                doc.Status === "Completed") && (
                                <Tooltip title="Quality Check">
                                  <IconButton
                                    size="sm"
                                    variant="plain"
                                    onClick={() =>
                                      setQcModal({
                                        isOpen: true,
                                        document: doc,
                                      })
                                    }
                                  >
                                    <FactCheckIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            {doc.Status !== "Completed" && (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  onClick={() =>
                                    handleEditDocument(doc.DocumentID)
                                  }
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <IconButton
                                size="sm"
                                variant="plain"
                                onClick={() =>
                                  setDeleteModal({
                                    isOpen: true,
                                    document: doc,
                                  })
                                }
                                disabled={deleteLoading}
                              >
                                <DeleteIcon sx={{ color: "#c41c1c" }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Box>

              {/* Mobile Card View */}
              <Box sx={{ display: { xs: "block", lg: "none" } }}>
                <Stack spacing={2}>
                  {documents.map((doc) => (
                    <DocumentCard key={doc.DocumentID} doc={doc} />
                  ))}
                </Stack>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Upgrade Confirmation Modal */}
      <ConfirmationModal
        open={upgradeModal.isOpen}
        onClose={() => setUpgradeModal({ isOpen: false })}
        title="Upgrade Document"
        message={
          upgradeModal.document
            ? `Are you sure you want to upgrade "${upgradeModal.document.Title}" to the latest version? This action cannot be undone.`
            : "Are you sure you want to upgrade this document?"
        }
        actionLabel={upgradeLoading ? "Upgrading..." : "Upgrade"}
        cancelLabel="Cancel"
        onAction={() =>
          upgradeModal.document &&
          handleUpgradeDocument(upgradeModal.document.DocumentID)
        }
        loading={upgradeLoading}
        spinner={<CircularProgress size="sm" />}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false })}
        title="Delete Document"
        message={
          deleteModal.document
            ? `Are you sure you want to delete "${deleteModal.document.Title}"? This action cannot be undone.`
            : "Are you sure you want to delete this document?"
        }
        actionLabel={deleteLoading ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        onAction={() =>
          deleteModal.document &&
          handleDeleteDocument(deleteModal.document.DocumentID)
        }
        loading={deleteLoading}
        spinner={<CircularProgress size="sm" />}
      />

      {/* QC Modal */}
      {qcModal.document && (
        <QCModal
          isOpen={qcModal.isOpen}
          onClose={() => setQcModal({ isOpen: false, document: null })}
          documentId={qcModal.document.DocumentID}
          documentTitle={qcModal.document.Title}
          workspaceId={workspace_id}
        />
      )}
    </Page>
  );
};

export default DocumentOverview;
